import React, { useMemo } from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  AssessmentOutlined as AssessmentOutlinedIcon,
  DirectionsCarFilledOutlined as DirectionsCarFilledOutlinedIcon,
  Groups2Outlined as Groups2OutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  NotificationsActiveOutlined as NotificationsActiveOutlinedIcon,
  PersonOutlineRounded as PersonOutlineRoundedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  RouteOutlined as RouteOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  TaskAltOutlined as TaskAltOutlinedIcon,
  MarkUnreadChatAltOutlined as MarkUnreadChatAltOutlinedIcon,
  EventNoteOutlined as EventNoteOutlinedIcon,
  PaidOutlined as PaidOutlinedIcon,
  SensorsRounded as SensorsRoundedIcon,
} from "@mui/icons-material";
import GlobalDashboardSidebar from "../admin/GlobalDashboardSidebar";
import useGlobalSidebarItems from "../admin/useGlobalSidebar";
import MessageManagement from "../admin/messagemanagement/MessageManagement";
import { USER_ROLE_ROUTE_CONFIG } from "../users/userRoleConfig";
import { logout } from "../../store/authSlice";
import "../admin/dashboard-redesign.css";

const TASK_MENU_ITEMS = [
  { key: "all", label: "All Tasks" },
  { key: "create", label: "Create Task" },
  { key: "inventory", label: "Inventory" },
];

const MESSAGE_MENU_ITEMS = [
  { key: "compose", label: "Compose & Send" },
  { key: "templates", label: "Templates" },
  { key: "delivery-reports", label: "Delivery Reports" },
  { key: "scheduled", label: "Scheduled Messages" },
];

const EXPENSE_MENU_ITEMS = [
  { key: "create", label: "Create Expense", path: "/expense/create" },
  { key: "list", label: "Expense List", path: "/expense/list" },
  { key: "upload", label: "Upload Expense", path: "/expense/upload" },
];

const SENSOR_MENU_ITEMS = [
  { key: "list", label: "Sensor List", path: "/sensors/list" },
  { key: "create", label: "Add Sensor", path: "/sensors/create" },
  { key: "upload", label: "Upload Sensors", path: "/sensors/upload" },
];

const PLACEHOLDER_META = {
  templates: {
    title: "Templates",
    subtitle: "Saved message templates will appear here without changing current APIs.",
  },
  "delivery-reports": {
    title: "Delivery Reports",
    subtitle: "Delivery tracking UI can be placed here while keeping the same messaging backend.",
  },
  scheduled: {
    title: "Scheduled Messages",
    subtitle: "Scheduled message management can be surfaced here when connected to existing flows.",
  },
};

export default function MessagesPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useSelector((state) => state.auth.user);

  const section = searchParams.get("section") || "compose";
  const profileName = currentUser?.name || currentUser?.username || "User";
  const roleLabel = String(currentUser?.role || "User")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const sidebarItems = useGlobalSidebarItems();

  const placeholder = PLACEHOLDER_META[section];

  return (
    <div className="dashboard-page-shell">
      <GlobalDashboardSidebar
        items={sidebarItems}
        profileName={profileName}
        roleLabel={roleLabel}
        onSupport={() => navigate("/message")}
        onLogout={() => dispatch(logout())}
      />

      <main className="dashboard-content" style={{ gap: 18 }}>
        <Box>
          <Typography sx={{ mt: 0.2, color: "#7c8ba1", fontSize: 13.5 }}>
            Messages / {section === "compose" ? "Compose & Send" : MESSAGE_MENU_ITEMS.find((item) => item.key === section)?.label || "Compose & Send"}
          </Typography>
          <Typography sx={{ mt: 1.1, fontSize: { xs: "2rem", md: "2.35rem" }, fontWeight: 900, color: "#0f172a" }}>
            Messages
          </Typography>
          <Typography sx={{ mt: 0.6, color: "#64748b", fontSize: 15.5 }}>
            Send notifications and SMS to your users instantly
          </Typography>
        </Box>

        {section === "compose" ? (
          <MessageManagement embedded />
        ) : (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 4,
              p: 3,
              borderColor: "rgba(148,163,184,0.22)",
              boxShadow: "0 18px 40px rgba(15,23,42,0.05)",
            }}
          >
            <Stack spacing={1}>
              <Typography sx={{ fontSize: 24, fontWeight: 900, color: "#0f172a" }}>
                {placeholder?.title || "Section"}
              </Typography>
              <Typography sx={{ color: "#64748b", maxWidth: 760 }}>
                {placeholder?.subtitle}
              </Typography>
            </Stack>
          </Paper>
        )}
      </main>
    </div>
  );
}
